'use client'

import type { Editor } from '@tiptap/react'

import { cn } from '@/lib/utils'

import type { ToolbarConfig } from '../markdown-editor.interface'
import { EditorToolbarBlocks } from './editor-toolbar-blocks.atom'
import { EditorToolbarExtras } from './editor-toolbar-extras.atom'
import { EditorToolbarHeadings } from './editor-toolbar-headings.atom'
import { EditorToolbarLists } from './editor-toolbar-lists.atom'
import { EditorToolbarTextFormatting } from './editor-toolbar-text-formatting.atom'
import { EditorToolbarUndoRedo } from './editor-toolbar-undo-redo.atom'

export interface EditorToolbarProps {
  editor: Editor | null
  config: ToolbarConfig
  disabled?: boolean
  className?: string
}

/**
 * Rich text editor toolbar with configurable button groups
 */
export const EditorToolbar = ({
  editor,
  config,
  disabled = false,
  className,
}: EditorToolbarProps) => {
  if (!editor || config === 'none') {
    return null
  }

  const isMinimal = config === 'minimal'
  const isBasic = config === 'basic'
  const isComplete = config === 'complete'
  const isSystemPrompt = config === 'system-prompt'

  const isShowUndoRedo = !isMinimal
  const isShowHeadings = !isMinimal
  const isBasicHeadingsOnly = isBasic || isSystemPrompt
  const isShowTaskList = !isMinimal && !isSystemPrompt
  const isShowBlocks = isComplete
  const isShowLink = !isSystemPrompt
  const isShowColorPicker = isComplete

  return (
    <div
      role="toolbar"
      aria-label="Editor formatting toolbar"
      className={cn(
        'flex flex-wrap items-center gap-0.5 border-b border-border bg-background p-1',
        className
      )}
    >
      {isShowUndoRedo && <EditorToolbarUndoRedo editor={editor} isDisabled={disabled} />}

      <EditorToolbarTextFormatting editor={editor} isDisabled={disabled} />

      {isShowHeadings && (
        <EditorToolbarHeadings
          editor={editor}
          isDisabled={disabled}
          isBasicOnly={isBasicHeadingsOnly}
        />
      )}

      <EditorToolbarLists editor={editor} isDisabled={disabled} isShowTaskList={isShowTaskList} />

      {isShowBlocks && <EditorToolbarBlocks editor={editor} isDisabled={disabled} />}

      <EditorToolbarExtras
        editor={editor}
        isDisabled={disabled}
        isShowLink={isShowLink}
        isShowColorPicker={isShowColorPicker}
      />
    </div>
  )
}

EditorToolbar.displayName = 'EditorToolbar'
